import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, ChevronLeft, ChevronRight, Target, BarChart3, TrendingUp, 
  Shield, Users, DollarSign, CheckCircle, 
  ArrowRight, Star, Award, Zap, Globe, Lock
} from 'lucide-react';

interface Slide {
  id: string;
  title: string;
  subtitle?: string;
  content: React.ReactNode;
  icon: any;
  color: string;
}

interface PlanDirecteurPresentationProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function PlanDirecteurPresentation({ isOpen, onClose }: PlanDirecteurPresentationProps) {
  const [currentSlide, setCurrentSlide] = useState(0);

  const slides: Slide[] = [
    {
      id: 'title',
      title: 'Plan Directeur TI 2024-2029',
      subtitle: 'Standard ITIL - Transformation Digitale',
      icon: Target,
      color: '#0066FF',
      content: (
        <div className="text-center space-y-8">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
            className="w-32 h-32 mx-auto bg-gradient-to-br from-bz-blue to-blue-600 rounded-full flex items-center justify-center"
          >
            <Target className="w-16 h-16 text-white" />
          </motion.div>
          
          <div className="space-y-4">
            <h1 className="text-4xl font-bold text-bz-dark">Plan Directeur TI</h1>
            <h2 className="text-2xl text-bz-blue font-semibold">2024-2029</h2>
            <p className="text-xl text-gray-600">Standard ITIL - Transformation Digitale</p>
          </div>

          <div className="grid grid-cols-3 gap-6 max-w-2xl mx-auto">
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-xl">
              <div className="text-2xl font-bold text-blue-600">5</div>
              <div className="text-sm text-blue-800">Années</div>
            </div>
            <div className="bg-gradient-to-br from-green-50 to-green-100 p-4 rounded-xl">
              <div className="text-2xl font-bold text-green-600">87%</div>
              <div className="text-sm text-green-800">ROI</div>
            </div>
            <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-4 rounded-xl">
              <div className="text-2xl font-bold text-purple-600">99.9%</div>
              <div className="text-sm text-purple-800">Disponibilité</div>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'vision',
      title: 'Vision Stratégique',
      subtitle: 'Mission et Objectifs ITIL',
      icon: Star,
      color: '#F39C12',
      content: (
        <div className="space-y-8">
          <div className="bg-gradient-to-r from-orange-50 to-yellow-50 p-6 rounded-xl border-l-4 border-orange-500">
            <h3 className="text-xl font-bold text-orange-800 mb-4">🎯 Mission TI</h3>
            <p className="text-lg text-gray-700 italic">
              "Fournir des services TI de classe mondiale qui soutiennent la croissance de 25% du CA en 5 ans, 
              en optimisant l'efficacité opérationnelle et en garantissant la sécurité des données."
            </p>
          </div>

          <div className="grid grid-cols-2 gap-6">
            <div className="space-y-4">
              <h4 className="text-lg font-semibold text-bz-dark">Objectifs Stratégiques ITIL</h4>
              <div className="space-y-3">
                {[
                  { name: 'Service Strategy', desc: 'Alignement TI-Business', color: 'bg-blue-500' },
                  { name: 'Service Design', desc: 'Architecture orientée services', color: 'bg-green-500' },
                  { name: 'Service Transition', desc: 'Gestion du changement', color: 'bg-purple-500' },
                  { name: 'Service Operation', desc: 'Excellence opérationnelle', color: 'bg-orange-500' },
                  { name: 'CSI', desc: 'Amélioration continue', color: 'bg-red-500' }
                ].map((item, index) => (
                  <motion.div
                    key={item.name}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-center gap-3 p-3 bg-white rounded-lg shadow-sm"
                  >
                    <div className={`w-3 h-3 rounded-full ${item.color}`} />
                    <div>
                      <div className="font-medium text-gray-800">{item.name}</div>
                      <div className="text-sm text-gray-600">{item.desc}</div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>

            <div className="space-y-4">
              <h4 className="text-lg font-semibold text-bz-dark">Valeurs Clés</h4>
              <div className="space-y-3">
                {[
                  { icon: Zap, name: 'Agilité', desc: 'Réponse rapide aux besoins' },
                  { icon: Shield, name: 'Fiabilité', desc: '99.9% de disponibilité' },
                  { icon: Lock, name: 'Sécurité', desc: 'Conformité NIST et ISO' },
                  { icon: Globe, name: 'Innovation', desc: 'Adoption proactive tech' }
                ].map((item, index) => (
                  <motion.div
                    key={item.name}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-center gap-3 p-3 bg-white rounded-lg shadow-sm"
                  >
                    <item.icon className="w-5 h-5 text-bz-blue" />
                    <div>
                      <div className="font-medium text-gray-800">{item.name}</div>
                      <div className="text-sm text-gray-600">{item.desc}</div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'asis',
      title: 'État Actuel (ASIS)',
      subtitle: 'Analyse de Maturité ITIL',
      icon: BarChart3,
      color: '#E74C3C',
      content: (
        <div className="space-y-6">
          <div className="bg-gradient-to-r from-red-50 to-orange-50 p-6 rounded-xl border-l-4 border-red-500">
            <h3 className="text-xl font-bold text-red-800 mb-4">📊 Maturité ITIL Actuelle</h3>
            <p className="text-gray-700">Évaluation des processus ITIL sur une échelle de 1 à 5</p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {[
              { name: 'Service Desk', current: 2, target: 4, gap: -2 },
              { name: 'Incident Management', current: 2, target: 4, gap: -2 },
              { name: 'Problem Management', current: 1, target: 4, gap: -3 },
              { name: 'Change Management', current: 2, target: 4, gap: -2 },
              { name: 'Configuration Mgmt', current: 1, target: 4, gap: -3 },
              { name: 'Release Management', current: 1, target: 3, gap: -2 },
              { name: 'Service Level Mgmt', current: 2, target: 4, gap: -2 },
              { name: 'Capacity Management', current: 1, target: 3, gap: -2 },
              { name: 'Availability Mgmt', current: 2, target: 4, gap: -2 },
              { name: 'IT Service Continuity', current: 1, target: 4, gap: -3 }
            ].map((process, index) => (
              <motion.div
                key={process.name}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="bg-white p-4 rounded-lg shadow-sm border"
              >
                <div className="flex justify-between items-center mb-2">
                  <h4 className="font-medium text-gray-800">{process.name}</h4>
                  <span className={`px-2 py-1 rounded text-xs font-bold ${
                    process.gap <= -2 ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'
                  }`}>
                    Gap: {process.gap}
                  </span>
                </div>
                
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Actuel: {process.current}/5</span>
                    <span>Cible: {process.target}/5</span>
                  </div>
                  
                  <div className="flex gap-1">
                    {[1,2,3,4,5].map(level => (
                      <div
                        key={level}
                        className={`w-6 h-2 rounded ${
                          level <= process.current ? 'bg-red-500' :
                          level <= process.target ? 'bg-gray-300' : 'bg-gray-200'
                        }`}
                      />
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      )
    },
    {
      id: 'roadmap',
      title: 'Roadmap ITIL',
      subtitle: 'Transformation en 3 Phases',
      icon: TrendingUp,
      color: '#00B894',
      content: (
        <div className="space-y-6">
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-6 rounded-xl border-l-4 border-green-500">
            <h3 className="text-xl font-bold text-green-800 mb-4">🛣️ Transformation ITIL sur 5 Ans</h3>
            <p className="text-gray-700">Approche progressive pour minimiser les risques</p>
          </div>

          <div className="space-y-6">
            {[
              {
                phase: 'PHASE 1: FONDATIONS',
                years: '2024-2025',
                color: 'from-blue-500 to-blue-600',
                items: [
                  'Service Desk ITIL',
                  'Incident/Problem Management',
                  'Configuration Management (CMDB)',
                  'Monitoring de base'
                ]
              },
              {
                phase: 'PHASE 2: OPTIMISATION',
                years: '2026-2027',
                color: 'from-green-500 to-green-600',
                items: [
                  'ITSM Platform (ServiceNow)',
                  'Automation des processus',
                  'DevOps Pipeline',
                  'Cloud Migration (80%)'
                ]
              },
              {
                phase: 'PHASE 3: EXCELLENCE',
                years: '2028',
                color: 'from-purple-500 to-purple-600',
                items: [
                  'Continual Service Improvement',
                  'Innovation continue',
                  'Optimisation coûts',
                  'Excellence opérationnelle'
                ]
              }
            ].map((phase, index) => (
              <motion.div
                key={phase.phase}
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.2 }}
                className="relative"
              >
                <div className={`bg-gradient-to-r ${phase.color} text-white p-6 rounded-xl shadow-lg`}>
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-xl font-bold">{phase.phase}</h4>
                    <span className="bg-white/20 px-3 py-1 rounded-full text-sm font-medium">
                      {phase.years}
                    </span>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    {phase.items.map((item, itemIndex) => (
                      <motion.div
                        key={item}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.2 + itemIndex * 0.1 }}
                        className="flex items-center gap-2"
                      >
                        <CheckCircle className="w-4 h-4 text-white" />
                        <span className="text-sm">{item}</span>
                      </motion.div>
                    ))}
                  </div>
                </div>
                
                {index < 2 && (
                  <div className="flex justify-center my-4">
                    <ArrowRight className="w-6 h-6 text-gray-400" />
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      )
    },
    {
      id: 'budget',
      title: 'Investissements & ROI',
      subtitle: 'Budget 5 Ans et Retour sur Investissement',
      icon: DollarSign,
      color: '#2ECC71',
      content: (
        <div className="space-y-6">
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-6 rounded-xl border-l-4 border-green-500">
            <h3 className="text-xl font-bold text-green-800 mb-4">💰 Budget Total: 5.87M$ sur 5 Ans</h3>
            <p className="text-gray-700">Investissement progressif avec ROI de 87%</p>
          </div>

          <div className="grid grid-cols-2 gap-6">
            <div className="space-y-4">
              <h4 className="text-lg font-semibold text-bz-dark">Répartition Budget</h4>
              {[
                { name: 'Infrastructure', amount: '2.3M$', percentage: 39, color: 'bg-blue-500' },
                { name: 'Personnel', amount: '2.7M$', percentage: 46, color: 'bg-green-500' },
                { name: 'Outils ITIL', amount: '550K$', percentage: 9, color: 'bg-purple-500' },
                { name: 'Formation', amount: '320K$', percentage: 6, color: 'bg-orange-500' }
              ].map((item, index) => (
                <motion.div
                  key={item.name}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white p-4 rounded-lg shadow-sm"
                >
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-medium text-gray-800">{item.name}</span>
                    <span className="font-bold text-bz-blue">{item.amount}</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${item.percentage}%` }}
                      transition={{ delay: index * 0.1 + 0.3, duration: 0.8 }}
                      className={`h-2 rounded-full ${item.color}`}
                    />
                  </div>
                  <div className="text-sm text-gray-600 mt-1">{item.percentage}% du budget total</div>
                </motion.div>
              ))}
            </div>

            <div className="space-y-4">
              <h4 className="text-lg font-semibold text-bz-dark">ROI Attendu</h4>
              <div className="space-y-3">
                {[
                  { name: 'Économies Opérationnelles', amount: '2.8M$', desc: 'Réduction coûts 30%', color: 'text-green-600' },
                  { name: 'Gains Productivité', amount: '1.5M$', desc: 'Efficacité +25%', color: 'text-blue-600' },
                  { name: 'Réduction Incidents', amount: '800K$', desc: 'Temps perdu -60%', color: 'text-purple-600' }
                ].map((item, index) => (
                  <motion.div
                    key={item.name}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-white p-4 rounded-lg shadow-sm border-l-4 border-green-500"
                  >
                    <div className="flex justify-between items-center mb-1">
                      <span className="font-medium text-gray-800">{item.name}</span>
                      <span className={`font-bold ${item.color}`}>{item.amount}</span>
                    </div>
                    <div className="text-sm text-gray-600">{item.desc}</div>
                  </motion.div>
                ))}
              </div>

              <div className="bg-gradient-to-r from-green-100 to-emerald-100 p-4 rounded-lg border border-green-300">
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-700">ROI Total: 5.1M$</div>
                  <div className="text-lg text-green-600">87% Retour sur Investissement</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'kpis',
      title: 'KPIs & Performance',
      subtitle: 'Indicateurs de Succès ITIL',
      icon: Award,
      color: '#9B59B6',
      content: (
        <div className="space-y-6">
          <div className="bg-gradient-to-r from-purple-50 to-indigo-50 p-6 rounded-xl border-l-4 border-purple-500">
            <h3 className="text-xl font-bold text-purple-800 mb-4">📈 Indicateurs de Performance ITIL</h3>
            <p className="text-gray-700">Métriques clés pour mesurer le succès de la transformation</p>
          </div>

          <div className="grid grid-cols-2 gap-6">
            {[
              { name: 'Disponibilité', target: '99.9%', current: '95.2%', icon: Shield, color: 'bg-green-500' },
              { name: 'Temps Résolution P1', target: '<2h', current: '4.5h', icon: Zap, color: 'bg-blue-500' },
              { name: 'Satisfaction Client', target: '>90%', current: '78%', icon: Users, color: 'bg-purple-500' },
              { name: 'Succès Changements', target: '>95%', current: '82%', icon: CheckCircle, color: 'bg-orange-500' },
              { name: 'Coût par Utilisateur', target: '-20%', current: '+5%', icon: DollarSign, color: 'bg-red-500' },
              { name: 'Temps de Déploiement', target: '<1 jour', current: '3 jours', icon: TrendingUp, color: 'bg-indigo-500' }
            ].map((kpi, index) => (
              <motion.div
                key={kpi.name}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white p-6 rounded-xl shadow-sm border"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className={`p-2 rounded-lg ${kpi.color}`}>
                    <kpi.icon className="w-5 h-5 text-white" />
                  </div>
                  <h4 className="font-semibold text-gray-800">{kpi.name}</h4>
                </div>
                
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Cible</span>
                    <span className="font-bold text-green-600">{kpi.target}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Actuel</span>
                    <span className="font-bold text-gray-800">{kpi.current}</span>
                  </div>
                  
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: '75%' }}
                      transition={{ delay: index * 0.1 + 0.5, duration: 1 }}
                      className="bg-gradient-to-r from-blue-500 to-green-500 h-2 rounded-full"
                    />
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      )
    }
  ];

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const currentSlideData = slides[currentSlide];

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 z-40"
          />
          
          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
          >
            <div className="bg-white rounded-xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden">
              {/* Header */}
              <div 
                className="p-6 text-white"
                style={{ backgroundColor: currentSlideData.color }}
              >
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-4">
                    <currentSlideData.icon className="w-8 h-8" />
                    <div>
                      <h2 className="text-2xl font-bold">{currentSlideData.title}</h2>
                      {currentSlideData.subtitle && (
                        <p className="text-white/90">{currentSlideData.subtitle}</p>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={onClose}
                    className="text-white hover:bg-white/10 rounded-full p-2 transition-colors"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>
                
                {/* Progress Bar */}
                <div className="mt-4">
                  <div className="flex justify-between text-sm mb-2">
                    <span>Slide {currentSlide + 1} sur {slides.length}</span>
                    <span>{Math.round(((currentSlide + 1) / slides.length) * 100)}%</span>
                  </div>
                  <div className="w-full bg-white/20 rounded-full h-2">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${((currentSlide + 1) / slides.length) * 100}%` }}
                      transition={{ duration: 0.3 }}
                      className="bg-white h-2 rounded-full"
                    />
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="p-8 max-h-[calc(90vh-200px)] overflow-y-auto">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={currentSlide}
                    initial={{ opacity: 0, x: 50 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -50 }}
                    transition={{ duration: 0.3 }}
                  >
                    {currentSlideData.content}
                  </motion.div>
                </AnimatePresence>
              </div>

              {/* Navigation */}
              <div className="flex justify-between items-center p-6 border-t bg-gray-50">
                <button
                  onClick={prevSlide}
                  disabled={currentSlide === 0}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-200 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Précédent
                </button>
                
                <div className="flex gap-2">
                  {slides.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentSlide(index)}
                      className={`w-3 h-3 rounded-full transition-colors ${
                        index === currentSlide ? 'bg-bz-blue' : 'bg-gray-300'
                      }`}
                    />
                  ))}
                </div>
                
                <button
                  onClick={nextSlide}
                  disabled={currentSlide === slides.length - 1}
                  className="flex items-center gap-2 px-4 py-2 bg-bz-blue hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
                >
                  Suivant
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
